import React, { useState, useCallback, useMemo } from "react";
import { Navigate } from "react-router-dom";
import { useDropzone } from "react-dropzone";
import Papa from "papaparse";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter
} from "@/components/ui/dialog";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow
} from "@/components/ui/table";
import { Upload, File as FileIcon, AlertTriangle, Check, X, Download } from "lucide-react";
import { toast } from "sonner";

const TYPES = [
  "relaties","klanten","leveranciers","prospects","suspects","leads",
  "zakelijk_partners","nevenvestigingen","contactpersonen","kenmerken",
  "gespreksverslagen","verkoopkansen","verkoopkansregels",
  "verkoopfacturen","verkoopfactuurregels","producten_afleiden","gebruikers",
];
const ORDER_INDEX = Object.fromEntries(TYPES.map((t, i) => [t, i]));

const FILENAME_MAP = {
  "alle_organisaties": "relaties",
  "alle_klanten": "klanten",
  "alle_leveranciers": "leveranciers",
  "alle_prospects": "prospects",
  "alle_suspects": "suspects",
  "alle_leads": "leads",
  "alle_zakelijk_partners": "zakelijk_partners",
  "alle_nevenvestigingen": "nevenvestigingen",
  "alle_contactpersonen": "contactpersonen",
  "alle_kenmerken": "kenmerken",
  "alle_gespreksverslagen": "gespreksverslagen",
  "alle_verkoopkansen": "verkoopkansen",
  "alle_verkoopkansregels": "verkoopkansregels",
  "alle_verkoopfacturen": "verkoopfacturen",
  "alle_verkoopfactuurregels": "verkoopfactuurregels",
  "gebruikers": "gebruikers",
};

const detectType = (name) => {
  const base = name.toLowerCase().replace(/\.csv$/, "");
  for (const k of Object.keys(FILENAME_MAP)) if (base.includes(k)) return FILENAME_MAP[k];
  return "";
};

const stripBOM = (s) => s.replace(/^\uFEFF/, "");

function parseCsv(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const text = stripBOM(reader.result);
      Papa.parse(text, {
        delimiter: ";", header: true, skipEmptyLines: true, quoteChar: '"',
        complete: (res) => resolve({ rows: res.data, fields: res.meta.fields || [] }),
        error: reject,
      });
    };
    reader.onerror = reject;
    reader.readAsText(file, "utf-8");
  });
}

function downloadErrors(name, errors) {
  const csv = "row;error\n" + errors.map(e => `${e.row};"${(e.error||'').replace(/"/g,'""')}"`).join("\n");
  const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url; a.download = `${name}_fouten.csv`; a.style.display = "none";
  document.body.appendChild(a); a.click();
  setTimeout(() => { document.body.removeChild(a); URL.revokeObjectURL(url); }, 1500);
}

export default function Import() {
  const { user } = useAuth();
  const [files, setFiles] = useState([]); // {id, file, type, rows, fields, status, progress, result}
  const [confirmOpen, setConfirmOpen] = useState(false);

  const onDrop = useCallback(async (accepted) => {
    const items = await Promise.all(accepted.map(async (f) => {
      const { rows, fields } = await parseCsv(f);
      return {
        id: `${f.name}-${f.size}-${Date.now()}-${Math.random().toString(36).slice(2,7)}`,
        file: f, type: detectType(f.name),
        rows, fields, status: "ready", progress: 0, result: null,
      };
    }));
    setFiles((x) => [...x, ...items]);
  }, []);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop, accept: { "text/csv": [".csv"], "application/vnd.ms-excel": [".csv"] }, multiple: true,
  });

  const setType = (id, type) => setFiles((x) => x.map(f => f.id === id ? { ...f, type } : f));
  const remove = (id) => setFiles((x) => x.filter(f => f.id !== id));

  const orderWarning = useMemo(() => {
    const idxs = files.filter(f => f.type).map(f => ORDER_INDEX[f.type]);
    for (let i = 1; i < idxs.length; i++) if (idxs[i] < idxs[i-1]) return true;
    return false;
  }, [files]);

  const ready = files.length > 0 && files.every(f => f.type);

  const importAll = async () => {
    setConfirmOpen(false);
    // sort by required order
    const ordered = [...files].sort((a, b) => ORDER_INDEX[a.type] - ORDER_INDEX[b.type]);
    for (const f of ordered) {
      setFiles(x => x.map(i => i.id === f.id ? { ...i, status: "importing", progress: 0 } : i));
      const totals = { totaal: 0, nieuw: 0, bijgewerkt: 0, overgeslagen: 0, errors: [] };
      const BATCH = 500;
      try {
        if (f.type === "producten_afleiden") {
          const { data } = await api.post("/import", { file_type: f.type, rows: [] });
          totals.totaal = data.totaal; totals.nieuw = data.nieuw;
          totals.bijgewerkt = data.bijgewerkt; totals.overgeslagen = data.overgeslagen;
          totals.errors = data.errors || [];
          setFiles(x => x.map(i => i.id === f.id ? { ...i, progress: 100 } : i));
        } else {
          for (let i = 0; i < f.rows.length; i += BATCH) {
            const batch = f.rows.slice(i, i + BATCH);
            const { data } = await api.post("/import", { file_type: f.type, rows: batch });
            totals.totaal += data.totaal;
            totals.nieuw += data.nieuw;
            totals.bijgewerkt += data.bijgewerkt;
            totals.overgeslagen += data.overgeslagen;
            totals.errors = totals.errors.concat((data.errors||[]).map(e => ({...e, row: e.row + i})));
            const progress = Math.min(100, Math.round(((i + batch.length) / f.rows.length) * 100));
            setFiles(x => x.map(it => it.id === f.id ? { ...it, progress } : it));
          }
        }
        setFiles(x => x.map(i => i.id === f.id ? { ...i, status: "done", result: totals, progress: 100 } : i));
      } catch (e) {
        setFiles(x => x.map(i => i.id === f.id ? { ...i, status: "error", result: { ...totals, errors: [{ row: -1, error: e?.response?.data?.detail || e.message }] } } : i));
      }
    }
    toast.success("Import voltooid");
  };

  if (user && user.rol !== "admin") return <Navigate to="/dashboard" replace />;

  return (
    <div className="space-y-6" data-testid="import-page">
      <div>
        <div className="text-sm text-muted-foreground">Beheer</div>
        <h1 className="font-display text-4xl font-medium tracking-tight mt-1">Import / Migratie</h1>
        <p className="text-sm text-muted-foreground mt-2 max-w-2xl">
          PerfectView CSV-export importeren. Scheidingsteken <code className="px-1 bg-secondary rounded">;</code>,
          UTF-8 met BOM, velden in dubbele quotes. Parsen client-side; idempotent upsert in batches van 500.
        </p>
      </div>

      <div {...getRootProps()}
        className={`border-2 border-dashed rounded-xl p-10 text-center cursor-pointer transition-colors
          ${isDragActive ? "border-primary bg-primary/5" : "border-border hover:border-primary/50 hover:bg-secondary/30"}`}
        data-testid="dropzone">
        <input {...getInputProps()} data-testid="dropzone-input" />
        <Upload className="mx-auto h-10 w-10 text-muted-foreground mb-3" />
        <div className="font-medium">Sleep CSV-bestanden hierheen</div>
        <div className="text-sm text-muted-foreground mt-1">of klik om te selecteren · meerdere tegelijk toegestaan</div>
      </div>

      {orderWarning && (
        <div className="flex items-start gap-2 p-3 bg-accent/15 border border-accent/40 rounded-lg text-sm">
          <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0 text-accent-foreground" />
          <div>
            Bestanden staan niet in de aanbevolen volgorde. Bij importeren worden ze automatisch
            geordend: relaties → rol-bestanden → contactpersonen → kenmerken → activiteiten →
            verkoopkansen(+regels) → verkoopfacturen(+regels) → producten afleiden → gebruikers.
          </div>
        </div>
      )}

      {files.length > 0 && (
        <div className="bg-card border border-border rounded-xl overflow-hidden">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Bestand</TableHead>
                <TableHead>Type</TableHead>
                <TableHead className="text-right">Rijen</TableHead>
                <TableHead>Kolommen</TableHead>
                <TableHead>Voortgang</TableHead>
                <TableHead>Resultaat</TableHead>
                <TableHead></TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {files.map((f) => (
                <TableRow key={f.id} data-testid={`file-row-${f.id}`}>
                  <TableCell className="font-medium flex items-center gap-2">
                    <FileIcon className="h-4 w-4 text-muted-foreground" />
                    <span className="truncate max-w-[200px]">{f.file.name}</span>
                  </TableCell>
                  <TableCell>
                    <Select value={f.type} onValueChange={(v) => setType(f.id, v)} disabled={f.status === "importing"}>
                      <SelectTrigger className="w-[200px]" data-testid={`type-select-${f.id}`}>
                        <SelectValue placeholder="Kies type…" />
                      </SelectTrigger>
                      <SelectContent>{TYPES.map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
                    </Select>
                  </TableCell>
                  <TableCell className="text-right text-muted-foreground">{f.rows.length}</TableCell>
                  <TableCell className="text-xs text-muted-foreground max-w-[260px] truncate" title={f.fields.join(", ")}>
                    {f.fields.slice(0, 4).join(", ")}{f.fields.length > 4 ? "…" : ""}
                  </TableCell>
                  <TableCell className="w-[180px]">
                    {f.status === "ready" && <Badge variant="outline">klaar</Badge>}
                    {f.status === "importing" && <Progress value={f.progress} className="h-2" />}
                    {f.status === "done" && <Badge className="bg-primary"><Check className="h-3 w-3 mr-1" />klaar</Badge>}
                    {f.status === "error" && <Badge variant="destructive">fout</Badge>}
                  </TableCell>
                  <TableCell className="text-xs">
                    {f.result && (
                      <div className="space-y-0.5">
                        <div>{f.result.nieuw} nieuw · {f.result.bijgewerkt} bijgewerkt</div>
                        <div className="text-muted-foreground">{f.result.overgeslagen} overgeslagen · {f.result.errors.length} fout</div>
                        {f.result.errors.length > 0 && (
                          <button onClick={() => downloadErrors(f.file.name, f.result.errors)}
                                  className="text-primary hover:underline inline-flex items-center gap-1 mt-1">
                            <Download className="h-3 w-3" /> fouten
                          </button>
                        )}
                      </div>
                    )}
                  </TableCell>
                  <TableCell>
                    {f.status !== "importing" && (
                      <Button variant="ghost" size="icon" onClick={() => remove(f.id)} data-testid={`remove-${f.id}`}>
                        <X className="h-4 w-4" />
                      </Button>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>

          {files.length > 0 && (
            <details className="border-t border-border p-3">
              <summary className="cursor-pointer text-sm font-medium">Preview eerste bestand (10 rijen)</summary>
              <div className="mt-3 overflow-x-auto">
                <table className="text-xs border border-border">
                  <thead className="bg-secondary">
                    <tr>{(files[0].fields || []).slice(0, 8).map(c => <th key={c} className="text-left px-2 py-1 border-r border-border">{c}</th>)}</tr>
                  </thead>
                  <tbody>
                    {files[0].rows.slice(0, 10).map((r, i) => (
                      <tr key={i} className="border-t border-border">
                        {files[0].fields.slice(0, 8).map(c => <td key={c} className="px-2 py-1 border-r border-border max-w-[150px] truncate">{String(r[c] ?? "")}</td>)}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </details>
          )}
        </div>
      )}

      {files.length > 0 && (
        <div className="flex justify-end">
          <Button size="lg" disabled={!ready} onClick={() => setConfirmOpen(true)} data-testid="import-start">
            Importeren
          </Button>
        </div>
      )}

      <Dialog open={confirmOpen} onOpenChange={setConfirmOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>Bevestig import</DialogTitle></DialogHeader>
          <div className="text-sm space-y-2">
            <p>Je staat op het punt {files.length} bestand(en) te importeren met in totaal{" "}
              <strong>{files.reduce((s, f) => s + f.rows.length, 0)}</strong> rijen.</p>
            <p>Bestaande records met dezelfde id worden bijgewerkt; nieuwe id's worden toegevoegd.</p>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setConfirmOpen(false)}>Annuleren</Button>
            <Button onClick={importAll} data-testid="confirm-import">Ja, importeer</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
